import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useAppDispatch } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { NotificationSettingsForm } from "./NotificationSettingsForm"
import {
    useNotificationPreferences,
    useUpdateNotificationPreferences,
} from "./NotificationSettingsQueries"
import { NotificationSettingsDraft, toNotificationSettingsDraft } from "./slice"

export const NotificationSettingsModal = ({ onClose }: { onClose: () => void }) => {
    const { preferences, isLoading } = useNotificationPreferences()
    const { updateNotificationPreferences, isMutating } = useUpdateNotificationPreferences()
    const dispatch = useAppDispatch()
    const [draft, setDraft] = useState<NotificationSettingsDraft | null>(null)

    useEffect(() => {
        if (preferences && !draft) setDraft(toNotificationSettingsDraft(preferences))
    }, [preferences, draft])

    const handleSave = async () => {
        if (!draft) return
        try {
            await updateNotificationPreferences(draft)
            dispatch(
                setGlobalNotificationContext({
                    message: "Notification settings saved.",
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 5000,
                })
            )
            onClose()
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to save notification settings. Please try again.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        }
    }

    return createPortal(
        <HighPriorityNovaModalContainer
            title="Notification Settings"
            closeModal={onClose}
            className="w-[56rem] max-w-[90vw]"
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary title="Cancel" onClick={onClose} disabled={isMutating} />
                    <ButtonPrimary
                        title="Save Changes"
                        onClick={handleSave}
                        disabled={isMutating || !draft}
                        isLoading={isMutating}
                    />
                </div>
            }
        >
            {isLoading || !draft ? (
                <div className="flex items-center justify-center py-16">
                    <div className="loader" />
                </div>
            ) : (
                <NotificationSettingsForm
                    value={draft}
                    onChange={setDraft}
                    currentSilenceUntil={preferences?.silenceUntil}
                />
            )}
        </HighPriorityNovaModalContainer>,
        document.getElementById("portal")!
    )
}
